<script setup lang="ts">
import { computed } from 'vue';

import { useGlobalStyle } from '@/store/templateData';
import IconFa6SolidImage from '~icons/fa6-solid/image';

interface ModuleHostIntroductionProps {
  module: {
    components: {
      title: {
        text: string;
      };
      subtitle: {
        text: string;
      };
      description: {
        text: string;
      };
      image: {
        src: string;
      };
    };
  };
}

defineOptions({
  name: 'ModuleHostIntroductionDisplay'
});

const props = withDefaults(defineProps<ModuleHostIntroductionProps>(), {
  module: () => ({
    components: {
      title: {
        text: '',
      },
      subtitle: {
        text: '',
      },
      description: {
        text: '',
      },
      image: {
        src: '',
      },
    },
  }),
});

const components = computed(() => props.module.components);
const needRender = computed(() => [
  components.value.title.text.trim(),
  components.value.subtitle.text.trim(),
  components.value.description.text.trim(),
  components.value.image.src,
].some(Boolean));

const { titleStyle, subtitleStyle, descriptionStyle } = useGlobalStyle();
</script>

<template>
<div class="module-host-introduction-display" v-if="needRender">
  <div
    class="module-host-introduction-display__title"
    v-if="components.title.text"
    :style="titleStyle"
  >
    {{ components.title.text }}
  </div>
  <div
    class="module-host-introduction-display__subtitle"
    v-if="components.subtitle.text"
    :style="subtitleStyle"
  >
    {{ components.subtitle.text }}
  </div>
  <div class="module-host-introduction-display__image-box">
    <template v-if="components.image.src">
      <div class="module-host-introduction-display__image-box__host">
        <el-image class="w-full h-full" :src="components.image.src" fit="cover"/>
      </div>
    </template>
    <template v-else>
      <div class="module-host-introduction-display__image-box__placeholder">
        <IconFa6SolidImage class="text-8xl text-gray-700"/>
        <!---
          span.text-gray-700 {{ $t("cms_setting.display.host_introduction_image_placeholder") }}
        -->
      </div>
    </template>
  </div>
  <div
    class="module-host-introduction-display__desc"
    v-if="components.description.text"
    :style="descriptionStyle"
  >
    {{ components.description.text }}
  </div>
</div>
</template>
<style lang="scss" scoped>
$desktop-fixed-width: 1075px;

.module-host-introduction-display {
  @apply py-4;

  &__title {
    @apply text-4xl text-center whitespace-pre-line;
  }

  &__subtitle {
    @apply text-xl text-left whitespace-pre-line;
  }

  &__desc {
    @apply text-base text-left whitespace-pre-line;
  }

  &__title,
  &__subtitle,
  &__desc {
    @apply mb-4 font-medium break-all;
    // mobile版圖片滿版，但文字要與其他 module 等寬 (留 20px 的 padding)
    @apply px-5;

    word-wrap: break-word;
  }

  &__title,
  &__subtitle {
    @apply font-medium;
  }

  &__image-box {
    @apply relative mb-4 bg-gray-600;

    @include aspect-ratio($height: 1, $width: 2);

    &__host,
    &__placeholder {
      @apply absolute inset-0 w-full h-full;
    }

    &__placeholder {
      @apply flex flex-col items-center justify-center;
    }
  }
}

// tablet
.tablet .module-host-introduction-display {
  @apply px-10;

  &__title,
  &__subtitle,
  &__desc {
    @apply px-0;
  }
}

// PC - 1024px
.laptop .module-host-introduction-display {
  padding-left: 10%;
  padding-right: 10%;

  @apply py-8;

  &__title,
  &__subtitle,
  &__desc {
    @apply px-0;
  }
}

// PC - 1440px
.desktop .module-host-introduction-display {
  width: $desktop-fixed-width;

  @apply py-8;

  &__title,
  &__subtitle,
  &__desc {
    @apply px-0;
  }
}
</style>
